// src/features/team/DelegationFormDialog.tsx
// Create a role delegation — form-kit dialog (docs/ux/14-forms.md). The
// delegate is picked from the team roster (UserPicker) instead of a raw
// profile-id text field.

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { useTeam } from '@react/hooks';
import { FormDialog, FormRow, AutoGrowTextarea } from '@/components/form';
import { Select } from '@/components/Select';
import { UserPicker } from '@/components/UserPicker';
import { delegationReasonMap, validateDelegationForm } from './logic';

const REASON_OPTIONS = Object.entries(delegationReasonMap).map(([value, labelKey]) => ({ value, labelKey }));

export interface DelegationFormDialogProps {
  open: boolean;
  teamId: string;
  onClose: () => void;
  onCreated: () => void;
}

export function DelegationFormDialog({ open, teamId, onClose, onCreated }: DelegationFormDialogProps) {
  const { t } = useI18n();
  const store = useTeam();

  const [delegateId, setDelegateId] = useState('');
  const [reason, setReason] = useState('DELEGATION_REASON_VACATION');
  const [reasonDetails, setReasonDetails] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDelegateId('');
    setReason('DELEGATION_REASON_VACATION');
    setReasonDetails('');
    setStartAt('');
    setEndAt('');
    setErrors({});
    setError(null);
  }, [open]);

  const dirty = !!(delegateId || reasonDetails.trim() || startAt || endAt);

  const submit = async () => {
    const validationErrors = validateDelegationForm({ delegate_id: delegateId, start_at: startAt, end_at: endAt });
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await store.CreateRoleDelegation(teamId, {
        delegate_id: delegateId,
        reason,
        reason_details: reasonDetails.trim(),
        start_at: startAt,
        end_at: endAt,
      } as never);
      onCreated();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('team.createDelegation')}
      canSubmit={!!delegateId && !!startAt && !!endAt}
      isSubmitting={submitting}
      dirty={dirty}
      aria-label={t('team.createDelegation')}
    >
      <div className="space-y-3 px-3.5 py-3">
        {error && <p className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">{error}</p>}
        <FormRow label={t('team.delegateProfileId')} labelWidth="sm">
          <UserPicker
            teamId={teamId}
            value={delegateId}
            onChange={(userId) => { setDelegateId(userId); setErrors((p) => ({ ...p, delegate_id: '' })); }}
            error={errors.delegate_id ? t(errors.delegate_id) : undefined}
          />
        </FormRow>
        <FormRow label={t('common.reason')} labelWidth="sm">
          <Select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            options={REASON_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) }))}
            fullWidth={false}
            className="max-w-xs"
            aria-label={t('common.reason')}
          />
        </FormRow>
        <FormRow label={t('team.delegationDetails')} labelWidth="sm">
          <AutoGrowTextarea
            value={reasonDetails}
            onChange={setReasonDetails}
            placeholder={t('team.delegationDetailsPlaceholder')}
            minRows={1}
          />
        </FormRow>
        <div className="grid grid-cols-2 gap-3">
          <FormRow label={t('common.from')} labelWidth="sm">
            <input
              type="datetime-local"
              value={startAt}
              onChange={(e) => { setStartAt(e.target.value); setErrors((p) => ({ ...p, start_at: '' })); }}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.start_at && <p className="mt-1 text-xs text-destructive">{t(errors.start_at)}</p>}
          </FormRow>
          <FormRow label={t('common.to')} labelWidth="sm">
            <input
              type="datetime-local"
              value={endAt}
              onChange={(e) => { setEndAt(e.target.value); setErrors((p) => ({ ...p, end_at: '' })); }}
              className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {errors.end_at && <p className="mt-1 text-xs text-destructive">{t(errors.end_at)}</p>}
          </FormRow>
        </div>
      </div>
    </FormDialog>
  );
}

export default DelegationFormDialog;
